import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaTimes, FaStar } from 'react-icons/fa';
import { useMunicipiosAutocomplete } from '../hooks/useMunicipios';

/**
 * Componente de seleção de município com autocomplete
 * @param {string} value - Município selecionado
 * @param {function} onChange - Callback quando município muda
 * @param {boolean} darkMode - Modo escuro ativo
 * @param {array} favoritos - Lista de municípios favoritos
 * @param {function} onToggleFavorito - Callback para adicionar/remover favorito
 * @param {function} isFavorito - Função para verificar se é favorito
 */
export function MunicipioSelector({ 
  value, 
  onChange, 
  darkMode, 
  favoritos = [],
  onToggleFavorito,
  isFavorito,
  previewMode = false,
  previewMunicipios = []
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showFavoritos, setShowFavoritos] = useState(false);
  const [hoveredSearchIdx, setHoveredSearchIdx] = useState(null);
  const [hoveredFavIdx, setHoveredFavIdx] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const { municipios: municipiosRemotos, loading: loadingRemoto } = useMunicipiosAutocomplete(previewMode ? '' : searchTerm);
  const normalizar = (texto = '') => texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const municipios = previewMode
    ? previewMunicipios
        .filter(muni => normalizar(muni.name_muni).startsWith(normalizar(searchTerm)))
        .slice(0, 15)
    : municipiosRemotos;
  const loading = previewMode ? false : loadingRemoto;

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setShowFavoritos(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectMunicipio = (muni) => {
    const nomeMuni = typeof muni === 'string' ? muni : muni.name_muni;
    onChange(nomeMuni, muni);
    setSearchTerm('');
    setShowDropdown(false);
    setShowFavoritos(false);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setShowDropdown(val.length >= 2);
    setShowFavoritos(false);
  };

  const handleToggleFavorito = (e, muni) => {
    e.stopPropagation();
    if (onToggleFavorito) {
      onToggleFavorito(muni);
    }
  };

  const containerStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: window.innerWidth < 768 ? 'stretch' : 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
    flexDirection: window.innerWidth < 768 ? 'column' : 'row',
    padding: window.innerWidth < 768 ? '0 0.75rem' : 0,
    boxSizing: 'border-box'
  };

  const currentMunicipioStyle = {
    background: darkMode
      ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
      : 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
    border: `2px solid ${darkMode ? '#90caf9' : '#1976d2'}`,
    borderRadius: 12,
    padding: window.innerWidth < 768 ? '0.65rem 0.85rem' : '0.75rem 1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: window.innerWidth < 768 ? 16 : 18,
    fontWeight: 600,
    color: darkMode ? '#90caf9' : '#1976d2',
    boxShadow: darkMode
      ? '0 2px 8px rgba(0,0,0,0.3)'
      : '0 2px 8px rgba(25, 118, 210, 0.15)',
    cursor: 'pointer',
    transition: 'all 0.2s',
    width: window.innerWidth < 768 ? '100%' : 'auto',
    justifyContent: 'space-between',
    boxSizing: 'border-box'
  };

  const searchBoxStyle = {
    position: 'relative',
    minWidth: window.innerWidth < 768 ? 'auto' : 300,
    maxWidth: window.innerWidth < 768 ? '100%' : 400,
    width: window.innerWidth < 768 ? '100%' : 'auto',
    boxSizing: 'border-box'
  };

  const inputStyle = {
    width: '100%',
    padding: window.innerWidth < 768 ? '0.65rem 2.5rem 0.65rem 2.25rem' : '0.75rem 3rem 0.75rem 2.5rem',
    borderRadius: 12,
    border: darkMode ? '2px solid #90caf9' : '2px solid #1976d2',
    fontSize: window.innerWidth < 768 ? 14 : 16,
    background: darkMode ? '#232526' : '#fff',
    color: darkMode ? '#90caf9' : '#1976d2',
    fontWeight: 500,
    outline: 'none',
    transition: 'all 0.2s',
    minHeight: 46,
    boxSizing: 'border-box'
  };

  const dropdownStyle = {
    position: 'absolute',
    top: 'calc(100% + 0.5rem)',
    left: 0,
    right: 0,
    maxHeight: window.innerWidth < 768 ? 300 : 400,
    overflowY: 'auto',
    background: darkMode ? '#2a2a2a' : '#fff',
    border: darkMode ? '1px solid #444' : '1px solid #ddd',
    borderRadius: 12,
    boxShadow: darkMode 
      ? '0 4px 16px rgba(0,0,0,0.4)'
      : '0 4px 16px rgba(0,0,0,0.1)',
    zIndex: 1000
  };

  const itemStyle = (isHovered) => ({
    padding: window.innerWidth < 768 ? '0.6rem 0.75rem' : '0.75rem 1rem',
    cursor: 'pointer',
    borderBottom: darkMode ? '1px solid #333' : '1px solid #eee',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: isHovered 
      ? (darkMode ? '#333' : '#f5f5f5')
      : 'transparent',
    color: darkMode ? '#f0f0f0' : '#333',
    transition: 'background 0.15s',
    fontSize: window.innerWidth < 768 ? 14 : 16
  });

  const buttonStyle = {
    padding: window.innerWidth < 768 ? '0.6rem 1rem' : '0.75rem 1.25rem',
    borderRadius: 10,
    border: 'none',
    fontSize: window.innerWidth < 768 ? 13 : 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    width: window.innerWidth < 768 ? '100%' : 'auto',
    justifyContent: 'center',
    minHeight: 44,
    touchAction: 'manipulation'
  };

  return (
    <div className="municipio-selector" style={containerStyle} ref={dropdownRef}>
      <label htmlFor="municipio-search" style={{
        fontWeight: 500, 
        fontSize: window.innerWidth < 768 ? 16 : 18, 
        color: darkMode ? '#f8fafc' : '#333',
        width: window.innerWidth < 768 ? '100%' : 'auto',
        textAlign: window.innerWidth < 768 ? 'left' : 'inherit'
      }}>
        Município:
      </label>

      {/* Município atual — só exibe quando já há seleção */}
      {value && (
      <div 
        className="municipio-selector-current"
        style={currentMunicipioStyle}
        role="button"
        tabIndex={0}
        onClick={() => setShowFavoritos(!showFavoritos)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setShowFavoritos(!showFavoritos);
          }
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
      >
        <span>{value}</span>
        {isFavorito && (
          <button
            type="button"
            onClick={(e) => handleToggleFavorito(e, value)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              minWidth: 44,
              minHeight: 44,
              justifyContent: 'center',
              display: 'flex',
              alignItems: 'center'
            }}
            title={isFavorito(value) ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
            aria-label={isFavorito(value) ? `Remover ${value} dos favoritos` : `Adicionar ${value} aos favoritos`}
          >
            <FaStar 
              size={18} 
              color={isFavorito(value) ? '#ffd700' : (darkMode ? '#666' : '#ccc')} 
            />
          </button>
        )}
      </div>
      )}

      {/* Botão de favoritos */}
      {favoritos.length > 0 && (
        <button
          type="button"
          onClick={() => {
            setShowFavoritos(!showFavoritos);
            setShowDropdown(false);
            setSearchTerm('');
          }}
          style={{
            ...buttonStyle,
            background: showFavoritos 
              ? (darkMode ? '#ffd700' : '#ffa500')
              : (darkMode ? '#3a3a3a' : '#e3f2fd'),
            color: showFavoritos 
              ? '#000'
              : (darkMode ? '#ffd700' : '#1976d2')
          }}
          title="Ver favoritos"
        >
          <FaStar size={14} />
          Favoritos ({favoritos.length})
        </button>
      )}

      {/* Busca */}
      <div className="municipio-selector-search" style={searchBoxStyle}>
        <FaSearch 
          style={{ 
            position: 'absolute', 
            left: '0.75rem', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: darkMode ? '#90caf9' : '#1976d2'
          }} 
        />
        <input
          id="municipio-search"
          ref={inputRef}
          type="text"
          aria-label="Buscar município"
          autoComplete="off"
          placeholder="Digite para buscar..."
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => {
            if (searchTerm.length >= 2) setShowDropdown(true);
          }}
          style={inputStyle}
        />
        {searchTerm && (
          <button
            type="button"
            aria-label="Limpar busca de município"
            onClick={() => {
              setSearchTerm('');
              setShowDropdown(false);
            }}
            style={{
              position: 'absolute',
              right: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: darkMode ? '#90caf9' : '#1976d2',
              padding: 4,
              width: 44,
              height: 44,
              justifyContent: 'center',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <FaTimes />
          </button>
        )}

        {/* Dropdown de resultados da busca */}
        {showDropdown && (
          <div style={dropdownStyle}>
            {loading ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: darkMode ? '#999' : '#666' }}>
                Buscando...
              </div>
            ) : municipios.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: darkMode ? '#999' : '#666' }}>
                Nenhum município encontrado
              </div>
            ) : (
              municipios.map((muni, idx) => {
                const municipioNome = muni.name_muni;
                const estadoCodigo = muni.code_state;
                
                // Mapear código para sigla do estado
                const codigoParaSigla = {
                  11: 'RO', 12: 'AC', 13: 'AM', 14: 'RR', 15: 'PA', 16: 'AP', 17: 'TO',
                  21: 'MA', 22: 'PI', 23: 'CE', 24: 'RN', 25: 'PB', 26: 'PE', 27: 'AL', 28: 'SE', 29: 'BA',
                  31: 'MG', 32: 'ES', 33: 'RJ', 35: 'SP',
                  41: 'PR', 42: 'SC', 43: 'RS',
                  50: 'MS', 51: 'MT', 52: 'GO', 53: 'DF'
                };

                return (
                  <div
                    key={idx}
                    style={itemStyle(hoveredSearchIdx === idx)}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelectMunicipio(muni)}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleSelectMunicipio(muni);
                      }
                    }}
                    onMouseEnter={() => setHoveredSearchIdx(idx)}
                    onMouseLeave={() => setHoveredSearchIdx(null)}
                  >
                    <span>
                      {municipioNome} <small style={{ opacity: 0.7 }}>- {codigoParaSigla[estadoCodigo] || estadoCodigo}</small>
                    </span>
                    {onToggleFavorito && <button
                      type="button"
                      aria-label={`Adicionar ${municipioNome} aos favoritos`}
                      onClick={(e) => handleToggleFavorito(e, muni)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 4
                      }}
                    >
                      <FaStar 
                        size={16} 
                        color={isFavorito(municipioNome) ? '#ffd700' : (darkMode ? '#555' : '#ddd')} 
                      />
                    </button>}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Dropdown de favoritos */}
        {showFavoritos && favoritos.length > 0 && (
          <div style={dropdownStyle}>
            <div style={{ 
              padding: '0.75rem 1rem', 
              background: darkMode ? '#1a1a1a' : '#f5f5f5',
              borderBottom: darkMode ? '2px solid #444' : '2px solid #ddd',
              fontWeight: 600,
              color: darkMode ? '#ffd700' : '#ff8c00',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FaStar size={14} color="#ffd700" />
              Meus Favoritos
            </div>
            {favoritos.map((fav, idx) => (
              <div
                key={idx}
                style={itemStyle(hoveredFavIdx === idx)}
                onClick={() => handleSelectMunicipio(fav.name_muni)}
                onMouseEnter={() => setHoveredFavIdx(idx)}
                onMouseLeave={() => setHoveredFavIdx(null)}
              >
                <span>{fav.name_muni}</span>
                <button
                  type="button"
                  aria-label={`Remover ${fav.name_muni} dos favoritos`}
                  onClick={(e) => handleToggleFavorito(e, fav.name_muni)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4
                  }}
                  title="Remover dos favoritos"
                >
                  <FaStar size={16} color="#ffd700" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
